'use client';

import React from 'react';
import { Command } from 'cmdk';
import { useCommandPalette } from '@/context/CommandPaletteContext';
import { useAllCommands, type Command as CommandType } from '@/lib/commands';
import { cn } from '@/lib/utils';
import { 
  MagnifyingGlassIcon,
  HomeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  PlayIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const CommandPalette: React.FC = () => {
  const { isOpen, closePalette } = useCommandPalette();
  const { commands, commandsByCategory } = useAllCommands();

  // Removed noisy render-time log to prevent console spam

  // Don't render if not open
  if (!isOpen) return null;

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'navigation':
        return <HomeIcon className="h-4 w-4" />;
      case 'clients':
        return <UserGroupIcon className="h-4 w-4" />;
      case 'content':
      case 'actions':
        return <DocumentTextIcon className="h-4 w-4" />;
      case 'settings':
        return <Cog6ToothIcon className="h-4 w-4" />;
      default:
        return <PlayIcon className="h-4 w-4" />;
    }
  };

  const renderCommand = (command: CommandType) => (
    <Command.Item
      key={command.id}
      value={`${command.name} ${command.keywords.join(' ')}`}
      onSelect={(value) => {
        console.log('Command selected:', value, command); // Debug log
        if (!command.disabled) {
          try {
            command.action();
            closePalette(); // Ensure palette closes after action
          } catch (error) {
            console.error('Command action failed:', error);
          }
        }
      }}
      disabled={command.disabled}
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-md px-4 py-3 text-sm transition-colors",
        "hover:bg-primary/10 hover:text-foreground",
        "data-[selected=true]:bg-primary/15 data-[selected=true]:text-foreground",
        "aria-selected:bg-primary/15 aria-selected:text-foreground",
        "[&[data-selected]]:bg-primary/15 [&[data-selected]]:text-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "group text-foreground",
        !command.disabled && "cursor-pointer pointer-events-auto"
      )}
      style={{ pointerEvents: command.disabled ? 'none' : 'auto' }}
      onClick={(e) => {
        // Fallback click handler for better compatibility
        e.preventDefault();
        e.stopPropagation();
        console.log('Command clicked:', command); // Debug log
        if (!command.disabled) {
          try {
            command.action();
            closePalette();
          } catch (error) {
            console.error('Command action failed:', error);
          }
        }
      }}
    >
      <div className="flex items-center gap-3 flex-1">
        {/* Icon */}
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted/30">
          {command.icon ? (
            <span className="text-base">{command.icon}</span>
          ) : (
            <div className="text-muted-foreground">
              {getCategoryIcon(command.category)}
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{command.name}</span>
          {command.description && (
            <span className="text-xs text-muted-foreground">
              {command.description}
            </span>
          )}
        </div>
      </div>

      {/* Shortcut or Arrow */}
      <div className="flex items-center gap-2">
        {command.shortcut && (
          <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border border-border/30 bg-muted/50 px-2 font-mono text-[10px] font-medium text-muted-foreground opacity-100 sm:flex">
            {command.shortcut}
          </kbd>
        )}
        <ArrowRightIcon className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 group-data-[selected]:opacity-100 group-aria-selected:opacity-100 transition-opacity duration-200" />
      </div>
    </Command.Item>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm animate-in fade-in-0 duration-200"
        onClick={(e) => {
          e.stopPropagation();
          closePalette();
        }}
      />
      
      {/* Command Palette */}
      <div 
        className="fixed left-[50%] top-[50%] z-[10000] w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] animate-in fade-in-0 zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <Command 
          className="liquid-glass shadow-xl border-0 overflow-hidden command-palette-root"
          shouldFilter={true}
          loop={true}
        >
          {/* Header */}
          <div className="flex flex-col space-y-2 text-center sm:text-left p-6 pb-2 border-b border-border/20">
            <h2 className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2 text-foreground">
              <MagnifyingGlassIcon className="h-5 w-5 text-foreground" />
              Command Palette
            </h2>
            <p className="text-sm text-muted-foreground">
              Type a command or search...
            </p>
          </div>

          {/* Search Input */}
          <div className="relative px-6 pt-2">
            <MagnifyingGlassIcon className="absolute left-9 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Command.Input
              placeholder="Type a command or search..."
              className="flex h-11 w-full rounded-md border border-border/20 bg-muted/10 px-10 py-3 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <Command.List className="max-h-[400px] overflow-y-auto p-6 pt-4 [&_[cmdk-item][data-selected='true']]:bg-primary/15 [&_[cmdk-item][aria-selected='true']]:bg-primary/15">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              No commands found.
            </Command.Empty>

            {/* Render commands by category */}
            {commandsByCategory.map((category) => {
              if (category.commands.length === 0) return null;

              return (
                <Command.Group key={category.name} heading={category.name} className="[&_[cmdk-group-heading]]:px-0 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
                  {category.commands.map(renderCommand)}
                </Command.Group>
              );
            })}
          </Command.List>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 pt-2 border-t border-border/20 text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <kbd className="pointer-events-none h-5 select-none items-center gap-1 rounded border border-border/30 bg-muted/50 px-2 font-mono text-[10px] font-medium text-muted-foreground opacity-100 flex">
                  ↑↓
                </kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="pointer-events-none h-5 select-none items-center gap-1 rounded border border-border/30 bg-muted/50 px-2 font-mono text-[10px] font-medium text-muted-foreground opacity-100 flex">
                  ↵
                </kbd>
                <span>Select</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="pointer-events-none h-5 select-none items-center gap-1 rounded border border-border/30 bg-muted/50 px-2 font-mono text-[10px] font-medium text-muted-foreground opacity-100 flex">
                  esc
                </kbd>
                <span>Close</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="pointer-events-none h-5 select-none items-center gap-1 rounded border border-border/30 bg-muted/50 px-2 font-mono text-[10px] font-medium text-muted-foreground opacity-100 flex">
                ⌘K
              </kbd>
              <span>to open</span>
            </div>
          </div>
        </Command>
      </div>
    </>
  );
};

export default CommandPalette;
